/**
 * Number/money display helpers for the Loss Runs tab.
 *
 * Total Paid & Reserved and Loss Excess the Retention are derived FE-side
 * and shown LIVE as the user types (Henry, jun-23): the backend only
 * recomputes `total_incurred` on row create, not on update, so the FE
 * owns these two figures for display. SIR by Community stays backend-owned
 * (`sir_by_community` — the matched location's SIR); the Loss Ratio card
 * is still a backend aggregate.
 */

/** Parse a money/number value ("$12,345.67", "12345", 12345) to a number.
 *  Blank / unparseable → 0. */
export function num(v: string | number | null | undefined): number {
    if (!v) return 0;
    const n = Number(String(v).replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
}

/** Total Paid & Reserved for a row = the sum of the four paid/reserved
 *  amounts. FE-computed and rendered live (see module header). */
export function computeTotalIncurred(
    paidIndemnity: string,
    paidExpense: string,
    reservedIndemnity: string,
    reservedExpense: string,
): number {
    return num(paidIndemnity) + num(paidExpense) + num(reservedIndemnity) + num(reservedExpense);
}

/** Render a number as USD with cents, always two decimals — the derived
 *  money cells have to match the editable breakdown columns they sum, which
 *  render their stored value verbatim through `formatMoneyMask` (OR-1436).
 *  Zero renders as an em dash so empty cells stay quiet.
 *
 *  Both fraction-digit bounds are pinned on purpose. Summing the four
 *  amounts is float arithmetic, so 50258.12 + 9432.40 lands on
 *  59690.520000000004; without an explicit maximum, `toLocaleString`
 *  defaults to 3 digits and the artifact leaks (and 0.1 + 0.2 would render
 *  with a single decimal). */
export function displayMoney(n: number): string {
    if (!Number.isFinite(n) || n === 0) return "—";
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Calendar year of an MM/DD/YYYY date string — the trailing 4-digit
 *  group. "—" when there's no complete year yet. Per Henry (Slack jun-16):
 *  Accident Year = YEAR(Incident Date), Report Year = YEAR(Report Date) —
 *  a plain reflection of the date in the same row, not a backend value. */
export function yearOf(usDate: string): string {
    const m = usDate.match(/(\d{4})\s*$/);
    return m ? m[1] : "—";
}

/** Loss Excess the Retention for a row = max(0, total_incurred −
 *  sir_by_community). Floored at 0. The total is FE-computed (a number)
 *  while `sirByCommunity` is the backend per-row value (a string, the
 *  matched location's SIR — the backend's effective-SIR resolution never
 *  uses the row's own historical `sir`); both inputs are accepted. */
export function lossExcessOf(totalIncurred: number | string, sirByCommunity: string): number {
    return Math.max(0, num(totalIncurred) - num(sirByCommunity));
}
